import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiPlus,
  mdiTrashCanOutline,
  mdiSwapHorizontal,
  mdiArrowDownBold,
} from "@mdi/js";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import ItemSearchField from "../items/item_search_field";
import { ItemModel } from "../models/item_model";
import {
  CreateTransformMasterApi,
  GetTransformMasterByIdApi,
  TransformLine,
  UpdateTransformMasterApi,
} from "./transform_master_repo";
import "../../../styles/responsive_shrink.css";

type LineRow = {
  item_code: string;
  item_name: string;
  qty: number;
};

const emptyLine = (): LineRow => ({ item_code: "", item_name: "", qty: 1 });

const TransformCreateScreen: FunctionComponent = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const navigation = useHistory();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [inputs, setInputs] = useState<LineRow[]>([emptyLine()]);
  const [outputs, setOutputs] = useState<LineRow[]>([emptyLine()]);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      setLoading(true);
      const res = await GetTransformMasterByIdApi(id!);
      if (res.success && res.data) {
        const map = (lines: TransformLine[]): LineRow[] =>
          (lines ?? []).length
            ? lines.map((l) => ({
                item_code: l.item_code,
                item_name: l.item_name,
                qty: l.qty,
              }))
            : [emptyLine()];
        setInputs(map(res.data.inputs));
        setOutputs(map(res.data.outputs));
        setRemarks(res.data.remarks ?? "");
      } else {
        showToast({ type: ToastType.error, text: res.error || "Not found" });
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const asItem = (code: string, name: string): ItemModel | undefined =>
    code ? ({ item_code: code, name } as ItemModel) : undefined;

  const validate = (): string | null => {
    const cleanInputs = inputs.filter((l) => l.item_code);
    const cleanOutputs = outputs.filter((l) => l.item_code);
    if (cleanInputs.length === 0) return "Add at least one input item";
    if (cleanOutputs.length === 0) return "Add at least one output item";

    const dupCheck = (lines: LineRow[], label: string): string | null => {
      const seen = new Set<string>();
      for (const l of lines) {
        if (!Number.isFinite(l.qty) || l.qty <= 0)
          return `${label} ${l.item_code} quantity must be greater than 0`;
        if (seen.has(l.item_code))
          return `Duplicate ${label} item ${l.item_code}`;
        seen.add(l.item_code);
      }
      return null;
    };
    const inErr = dupCheck(cleanInputs, "input");
    if (inErr) return inErr;
    const outErr = dupCheck(cleanOutputs, "output");
    if (outErr) return outErr;

    const inputCodes = new Set(cleanInputs.map((l) => l.item_code));
    for (const o of cleanOutputs) {
      if (inputCodes.has(o.item_code))
        return "An item cannot be both an input and an output";
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      showToast({ type: ToastType.warning, text: err });
      return;
    }

    const clean = (lines: LineRow[]) =>
      lines
        .filter((l) => l.item_code)
        .map((l) => ({
          item_code: l.item_code,
          item_name: l.item_name,
          qty: l.qty,
        }));

    const payload = {
      inputs: clean(inputs),
      outputs: clean(outputs),
      remarks: remarks.trim() || undefined,
    };

    setSaving(true);
    const res = isEdit
      ? await UpdateTransformMasterApi(id!, payload)
      : await CreateTransformMasterApi(payload);
    setSaving(false);

    if (res.success) {
      showToast({
        type: ToastType.success,
        text: res.message || "Transform saved",
      });
      navigation.goBack();
    } else {
      showToast({ type: ToastType.error, text: res.error });
    }
  };

  const LineSection = ({
    title,
    hint,
    lines,
    setLines,
  }: {
    title: string;
    hint: string;
    lines: LineRow[];
    setLines: React.Dispatch<React.SetStateAction<LineRow[]>>;
  }) => {
    const update = (index: number, patch: Partial<LineRow>) =>
      setLines((prev) =>
        prev.map((l, i) => (i === index ? { ...l, ...patch } : l)),
      );
    const idPrefix = title.includes("Consumes")
      ? "transform_input"
      : "transform_output";
    return (
      <section id={`${idPrefix}_section`} className="bg-white rounded-md p-4">
        <div className="flex justify-between items-center mb-1">
          <h2 className="font-semibold">{title}</h2>
          <button
            id={`${idPrefix}_add_item_btn`}
            type="button"
            onClick={() => setLines((prev) => [...prev, emptyLine()])}
            className="h-9 px-3 border rounded flex items-center gap-1 text-sm"
          >
            <Icon path={mdiPlus} size={0.8} />
            Add Item
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-3">{hint}</p>
        <div className="space-y-3">
          {lines.map((l, index) => (
            <div
              id={`${idPrefix}_line_${index}`}
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_140px_44px] gap-3 items-end"
            >
              <div className="grid gap-1">
                <label className="text-sm">Item</label>
                <ItemSearchField
                  placeholder="Select item"
                  value={asItem(l.item_code, l.item_name)}
                  onSelect={(item) =>
                    update(index, {
                      item_code: item.item_code,
                      item_name: item.name,
                    })
                  }
                />
              </div>
              <div className="grid gap-1">
                <label className="text-sm">Qty</label>
                <input
                  id={`${idPrefix}_qty_${index}`}
                  type="number"
                  min={1}
                  className="border rounded-md px-3 py-1"
                  value={l.qty}
                  onChange={(e) =>
                    update(index, { qty: Number(e.target.value) })
                  }
                />
              </div>
              <button
                id={`${idPrefix}_remove_btn_${index}`}
                type="button"
                onClick={() =>
                  setLines((prev) =>
                    prev.length > 1 ? prev.filter((_, i) => i !== index) : prev,
                  )
                }
                className="h-9 w-9 flex items-center justify-center rounded border border-red-300 text-red-600 hover:bg-red-50"
                title="Remove item"
              >
                <Icon path={mdiTrashCanOutline} size={0.8} />
              </button>
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="mobile-shrink">
      <section className="flex justify-between items-end">
        <h1 className="m-2 md:m-4 border-l-4 border-myPrimaryColor pl-2 font-bold text-2xl text-gray-500 flex items-center gap-3">
          <Icon path={mdiSwapHorizontal} size={1} />
          {isEdit ? "Edit Transform" : "Create Transform Item"}
        </h1>
      </section>

      <div id="transform_create_form" className="grid gap-4 p-2 md:p-5">
        <LineSection
          title="Consumes (Input Items)"
          hint="Stock of these item codes is consumed when the transform is applied."
          lines={inputs}
          setLines={setInputs}
        />

        <div className="flex justify-center">
          <Icon path={mdiArrowDownBold} size={1} className="text-gray-400" />
        </div>

        <LineSection
          title="Produces (Output Items)"
          hint="Stock of these item codes is produced. Many-to-one, one-to-many and one-to-one are all supported."
          lines={outputs}
          setLines={setOutputs}
        />

        <section className="bg-white rounded-md p-3 md:p-4">
          <label className="text-sm">Remarks</label>
          <textarea
            id="transform_remarks"
            className="border rounded-md px-3 py-1 w-full"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </section>

        <div className="flex justify-end gap-2">
          <button
            id="quit_btn"
            type="button"
            onClick={() => navigation.goBack()}
            className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-4 py-1.5"
          >
            Quit
          </button>
          <button
            id="submit_btn"
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm disabled:opacity-50"
          >
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>

      {(loading || saving) && <LoadingWidget />}
    </div>
  );
};

export default TransformCreateScreen;
